import {
  createDataSourceDefinition,
  type DataSourceDefinition,
  type FiltersDefinition,
  type RecordType,
  type SortingsDefinition,
} from '@factorialco/f0-react'
import { useMemo } from 'react'

import i18n from 'modules/core/lib/i18n'
import type { AtsJobPostingSalaryPeriodEnum } from 'generated/resources/graphql'

export interface SalaryFrequencyOption extends RecordType {
  id: AtsJobPostingSalaryPeriodEnum
  name: string
}

// Mirrors `Ats::JobPostingSalaryPeriod` (backend T::Enum). Reuses ATS-side
// translation keys; static map (not dynamic keys) so i18n usage is statically
// detectable.
const SALARY_PERIOD_LABELS: Record<AtsJobPostingSalaryPeriodEnum, () => string> = {
  annual: () => i18n.t('ats.vacancies.creation.salary_period_annual'),
  monthly: () => i18n.t('ats.vacancies.creation.salary_period_monthly'),
  daily: () => i18n.t('ats.vacancies.creation.salary_period_daily'),
}

const SALARY_PERIOD_VALUES: readonly AtsJobPostingSalaryPeriodEnum[] = [
  'annual',
  'monthly',
  'daily',
]

export const salaryFrequencyLabel = (value: AtsJobPostingSalaryPeriodEnum): string =>
  SALARY_PERIOD_LABELS[value]?.() ?? value

const useHeadcountSalaryFrequencySource = (): DataSourceDefinition<
  SalaryFrequencyOption,
  FiltersDefinition,
  SortingsDefinition
> => {
  return useMemo(
    () =>
      createDataSourceDefinition<SalaryFrequencyOption, FiltersDefinition, SortingsDefinition>({
        dataAdapter: {
          fetchData: async ({ search }) => {
            const records = SALARY_PERIOD_VALUES.map(
              (id): SalaryFrequencyOption => ({ id, name: salaryFrequencyLabel(id) })
            ).filter(
              (option) => !search || option.name.toLowerCase().includes(search.toLowerCase())
            )

            return Promise.resolve({ records })
          },
        },
      }),
    []
  )
}

export default useHeadcountSalaryFrequencySource
